"use client";

import * as React from "react";
import { Registry } from "@/cms/blocks/registry";
import type { BlockBase } from "@/cms/blocks/registry/types";
import { deepCloneJson, uid } from "../utils";
import type { BlockType } from "../types";

export function useBlockClipboard({
  blocks,
  allowedTypes,
  onChange,
  insert,
}: {
  blocks: BlockBase[];
  allowedTypes: BlockType[];
  onChange: (next: BlockBase[]) => void;
  insert?: (nextBlocks: BlockBase[]) => void;
}) {
  const lastCopiedTextRef = React.useRef<string | null>(null);

  const setCopiedFallback = React.useCallback(
    (payload: BlockBase | BlockBase[]) => {
      try {
        lastCopiedTextRef.current = JSON.stringify(payload, null, 2);
      } catch {
        lastCopiedTextRef.current = null;
      }
    },
    [],
  );

  const normalizePastedBlock = React.useCallback(
    (raw: unknown): BlockBase => {
      if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
        throw new Error("Invalid block payload. Expected a single block object.");
      }
      const type = (raw as any).type;
      if (typeof type !== "string") {
        throw new Error("Block type is missing or invalid.");
      }
      if (!allowedTypes.includes(type as BlockType)) {
        throw new Error(`Blocks of type "${type}" are not allowed here.`);
      }
      const def = Registry.get(type);
      if (!def) {
        throw new Error(`Unknown block type "${type}".`);
      }
      const schemaVersion =
        typeof (raw as any).schemaVersion === "number"
          ? (raw as any).schemaVersion
          : def.currentSchemaVersion;
      const data =
        (raw as any).data && typeof (raw as any).data === "object"
          ? deepCloneJson((raw as any).data)
          : {};
      const meta =
        (raw as any).meta && typeof (raw as any).meta === "object"
          ? deepCloneJson((raw as any).meta)
          : undefined;
      return {
        id: uid(),
        type,
        schemaVersion,
        data,
        ...(meta ? { meta } : {}),
      };
    },
    [allowedTypes],
  );

  const normalizePastedPayload = React.useCallback(
    (raw: unknown): BlockBase[] => {
      if (Array.isArray(raw)) {
        if (!raw.length) throw new Error("Clipboard list is empty.");
        return raw.map((item) => normalizePastedBlock(item));
      }
      return [normalizePastedBlock(raw)];
    },
    [normalizePastedBlock],
  );

  const appendBlocks = React.useCallback(
    (nextBlocks: BlockBase[]) => {
      if (insert) {
        insert(nextBlocks);
        return;
      }
      onChange([...blocks, ...nextBlocks]);
    },
    [blocks, insert, onChange],
  );

  const copyBlocksToClipboard = React.useCallback(
    async (payloadBlocks: BlockBase | BlockBase[]): Promise<boolean> => {
      setCopiedFallback(payloadBlocks);
      const payload = lastCopiedTextRef.current;
      if (!payload) {
        alert("Unable to copy this block right now.");
        return false;
      }
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(payload);
          return true;
        } catch (error) {
          console.warn("Clipboard write failed, falling back to in-app copy", error);
        }
      }
      alert("Clipboard is unavailable. You can still paste within this editor.");
      return false;
    },
    [setCopiedFallback],
  );

  const parseBlockText = React.useCallback(
    (text: string): BlockBase[] => {
      if (!text.trim()) {
        throw new Error("Clipboard is empty.");
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        throw new Error("Clipboard does not contain valid block JSON.");
      }
      return normalizePastedPayload(parsed);
    },
    [normalizePastedPayload],
  );

  const pasteBlockFromClipboard = React.useCallback(
    async (source: "button" | "shortcut") => {
      const tryTexts: Array<() => Promise<string | null>> = [
        async () => {
          if (typeof navigator === "undefined" || !navigator.clipboard?.readText) {
            return null;
          }
          try {
            const clipboardText = await navigator.clipboard.readText();
            return clipboardText || null;
          } catch (error) {
            console.warn("Clipboard read failed", error);
            return null;
          }
        },
        async () => lastCopiedTextRef.current ?? null,
      ];

      let text: string | null = null;
      for (const getter of tryTexts) {
        text = await getter();
        if (text && text.trim()) break;
      }

      if (!text) {
        alert("Nothing to paste. Copy a block first.");
        return;
      }

      try {
        const blockList = parseBlockText(text);
        appendBlocks(blockList);
      } catch (error) {
        const message = (error as Error)?.message || "Unable to paste block.";
        alert(message);
        return;
      }

      if (source === "button") {
        console.info("Block pasted at the end of the list.");
      }
    },
    [appendBlocks, parseBlockText],
  );

  const isEditableTarget = React.useCallback((target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    const tagName = target.tagName.toLowerCase();
    if (
      tagName === "input" ||
      tagName === "textarea" ||
      tagName === "select" ||
      target.isContentEditable
    ) {
      return true;
    }
    const contentEditableAncestor = target.closest("[contenteditable='true']");
    return Boolean(contentEditableAncestor);
  }, []);

  React.useEffect(() => {
    const handlePaste = (event: ClipboardEvent) => {
      if (isEditableTarget(event.target)) return;
      const text = event.clipboardData?.getData("text/plain");
      if (!text) return;
      try {
        const blockList = parseBlockText(text);
        event.preventDefault();
        appendBlocks(blockList);
      } catch {
        // ignore non-block clipboard content
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => {
      window.removeEventListener("paste", handlePaste);
    };
  }, [appendBlocks, isEditableTarget, parseBlockText]);

  return {
    copyBlockToClipboard: (block: BlockBase) => copyBlocksToClipboard(block),
    copyBlocksToClipboard,
    pasteBlockFromClipboard,
  };
}

